import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { ParticipantService } from '../../../services/participant.service';
import { CenterService } from '../../../services/center.service';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

export interface MinimatchSettingsDialogData {
  hasMinimatchInactive: boolean;
}

export interface PMWaitlistGroup {
  id: string;
  name: string;
  label: string;
  checked: boolean;
}

@Component({
  selector: 'app-minimatch-settings-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    FormsModule
  ],
  templateUrl: './minimatch-settings-dialog.component.html',
  styleUrls: ['./minimatch-settings-dialog.component.css']
})
export class MinimatchSettingsDialogComponent implements OnInit {
  allCrossoverParticipants = false;

  pmWaitlistGroups: PMWaitlistGroup[] = [];

  isProcessing = false;
  isTogglingActive = false;
  isLoadingCenters = true;

  // PM center names in the order we want to display them
  private readonly PM_CENTER_NAMES = ['NMSTP', 'NLBTP', 'NUTTP', 'NNYTP', 'NGRTP', 'NRDTP', 'NAMTP'];

  constructor(
    public dialogRef: MatDialogRef<MinimatchSettingsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MinimatchSettingsDialogData,
    private participantService: ParticipantService,
    private centerService: CenterService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadCenters();
  }

  private loadCenters(): void {
    this.centerService.getAllCenters().subscribe({
      next: (centers) => {
        // Filter to only PM centers and map to our interface
        // Use case-insensitive and trimmed comparison for robustness
        const pmCenters = centers.filter(c => {
          const normalizedName = c.name?.trim().toUpperCase();
          return this.PM_CENTER_NAMES.some(pmName => pmName.toUpperCase() === normalizedName);
        });

        // Sort by the order defined in PM_CENTER_NAMES
        this.pmWaitlistGroups = this.PM_CENTER_NAMES
          .map(name => pmCenters.find(c => c.name?.trim().toUpperCase() === name.toUpperCase()))
          .filter(c => c !== undefined)
          .map(center => ({
            id: center!.id,
            name: center!.name,
            label: center!.name,
            checked: false
          }));

        this.isLoadingCenters = false;
      },
      error: (err) => {
        console.error('Error loading centers:', err);
        this.snackBar.open('Error loading centers. Please try again.', 'Close', { duration: 5000 });
        this.isLoadingCenters = false;
      }
    });
  }

  get allPMGroupsChecked(): boolean {
    return this.pmWaitlistGroups.every(group => group.checked);
  }

  get somePMGroupsChecked(): boolean {
    return this.pmWaitlistGroups.some(group => group.checked) && !this.allPMGroupsChecked;
  }

  toggleAllPMGroups(): void {
    const newValue = !this.allPMGroupsChecked;
    this.pmWaitlistGroups.forEach(group => group.checked = newValue);
  }

  get hasSelections(): boolean {
    return this.allCrossoverParticipants || this.pmWaitlistGroups.some(group => group.checked);
  }

  applySettings(): void {
    if (!this.hasSelections) {
      this.snackBar.open('Please select at least one option', 'Close', { duration: 3000 });
      return;
    }

    this.isProcessing = true;

    const selectedPMGroupIds = this.pmWaitlistGroups
      .filter(group => group.checked)
      .map(group => group.id);

    this.participantService.setMinimatchInactive(
      this.allCrossoverParticipants,
      selectedPMGroupIds
    ).subscribe({
      next: (response) => {
        this.isProcessing = false;
        let message = response.message;

        if (response.deactivated) {
          const total = response.deactivated.pairs + response.deactivated.altruists +
                        response.deactivated.standaloneDonors + response.deactivated.standaloneRecipients;
          message = `Successfully excluded ${total} participants from minimatch`;
        }

        this.dialogRef.close({ success: true, message });
      },
      error: (err) => {
        this.isProcessing = false;
        console.error('Error setting minimatch inactive:', err);
        this.snackBar.open('Error updating participants. Please try again.', 'Close', {
          duration: 5000
        });
      }
    });
  }

  toggleAllInactiveToActive(): void {
    this.isTogglingActive = true;

    this.participantService.toggleMinimatchInactiveToActive().subscribe({
      next: (response) => {
        this.isTogglingActive = false;
        let message = response.message;

        if (response.activated) {
          const total = response.activated.pairs + response.activated.altruists +
                        response.activated.standaloneDonors + response.activated.standaloneRecipients;
          message = `Successfully activated ${total} participants`;
        }

        this.snackBar.open(message, 'Close', { duration: 5000 });
        this.dialogRef.close({ success: true, message });
      },
      error: (err) => {
        this.isTogglingActive = false;
        console.error('Error toggling minimatch status:', err);
        this.snackBar.open('Error activating participants. Please try again.', 'Close', {
          duration: 5000
        });
      }
    });
  }

  cancel(): void {
    this.dialogRef.close({ success: false });
  }
}
